import { context } from "@opentelemetry/api";
import { captureMessage } from "@sentry/react";
import { getAuthRedirectUrl } from "./standaloneConnect";

type RejectWithValue = (value: unknown, meta?: { status: number }) => unknown;

export interface FetchResult {
  data: unknown;
  status: number;
}

export const isFetchResult = (value: unknown): value is FetchResult => {
  const result =
    Boolean(value) &&
    typeof value === "object" &&
    value !== null &&
    "status" in value &&
    typeof (value as FetchResult).status === "number" &&
    "data" in value;
  return result;
};

// Module-level promise to coordinate CSRF token readiness across the app.
// Prevents race conditions where requests fire before the initial token fetch completes.
let csrfTokenPromise: Promise<string> | null = null;

// WorkOS auth lives in cookies + Redux, not browser token storage, so this util
// can't read it directly. App.tsx mirrors the Redux flag here so request-time
// gating works for both stored-token and WorkOS users.
let workOsAuthenticated = false;
export const setWorkOsAuthenticated = (authenticated: boolean) => {
  workOsAuthenticated = authenticated;
};
const getStoredSessionToken = () => sessionStorage.getItem("token") || "";
const getStoredAuthToken = () => getStoredSessionToken() || ""
const hasAuthSession = () => workOsAuthenticated || Boolean(getStoredAuthToken());

export const headers = ({
  tokenOverride,
  includeStoredToken = true,
}: {
  tokenOverride?: string;
  includeStoredToken?: boolean;
} = {}): Record<string, string> => {
  const token = tokenOverride ?? (includeStoredToken ? getStoredAuthToken() : "");
  const csrfToken = sessionStorage.getItem("csrfToken");
  if (token) {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": token,
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
    };
  }

  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
  };
};

export const fetchCsrfToken = async () => {
  // Deduplicate concurrent calls - if a fetch is already in flight, reuse its promise
  if (csrfTokenPromise) return csrfTokenPromise;

  csrfTokenPromise = fetch(`${import.meta.env.VITE_API_URL}/user/csrf-token`, {
    method: "GET",
    headers: headers(),
    credentials: "include",
  })
    .then(async (response) => {
      if (!response.ok) {
        captureMessage(`CSRF token request failed with status ${response.status}`);
        return "";
      }
      const body = await response.json();
      const csrfToken = body?.data?.csrfToken ?? "";
      sessionStorage.setItem("csrfToken", csrfToken);
      return csrfToken;
    })
    .catch((error) => {
      captureMessage(`Failed to fetch CSRF token: ${error}`);
      return "";
    })
    .finally(() => {
      csrfTokenPromise = null;
    });

  return csrfTokenPromise;
};

export const waitForCsrfToken = () => {
  // CSRF is only enforced on authenticated routes, so skip the fetch when signed out.
  if (!hasAuthSession()) return Promise.resolve();
  if (sessionStorage.getItem("csrfToken")) return Promise.resolve();
  if (csrfTokenPromise) return csrfTokenPromise;
  return fetchCsrfToken();
};

const isCsrfError = (status: number, body: Record<string, unknown> | null): boolean => {
  return status === 403 && (body?.status_message as string)?.toLowerCase().includes("csrf token");
};

const safeParseJson = async (response: Response): Promise<Record<string, unknown> | null> => {
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export const reusableFetch = async (
  url: string,
  method: string,
  data: unknown,
  rejectWithValue?: RejectWithValue,
  isArrayBuffer?: boolean,
  tokenOverride?: string,
): Promise<FetchResult | void | unknown> => {
  // The browser context manager does not follow `await`, so keep the caller's trace context for the requests below.
  // Otherwise they start new traces instead of joining the caller's span.
  const callerContext = context.active();
  await waitForCsrfToken();

  const fullUrl = `${import.meta.env.VITE_API_URL}/${url}`;
  const upperMethod = method.toUpperCase();

  const options: RequestInit = {
    method: upperMethod,
    headers: headers({ tokenOverride }),
    credentials: "include",
  };

  if (upperMethod !== "GET" && upperMethod !== "HEAD") {
    options.body = JSON.stringify(data || {});
  }

  try {
    const response = await context.with(callerContext, () => fetch(fullUrl, options));

    // Handle CSRF token expiry - retry with fresh token
    if (response.status === 403) {
      const body = await safeParseJson(response);
      if (isCsrfError(response.status, body)) {
        await fetchCsrfToken();
        const retryOptions: RequestInit = { ...options, headers: headers({ tokenOverride }) };
        const retryResponse = await context.with(callerContext, () => fetch(fullUrl, retryOptions));
        const retryData = isArrayBuffer ? await retryResponse.arrayBuffer() : await safeParseJson(retryResponse);
        if (!retryResponse.ok && rejectWithValue) {
          return rejectWithValue(retryData, { status: retryResponse.status });
        }
        return { data: retryData, status: retryResponse.status };
      }
      return rejectWithValue?.(body, { status: response.status });
    }

    // Handle redirects
    if (response.status === 302) {
      const body = await safeParseJson(response);
      window.location.replace(body?.redirectUrl as string);
      return;
    }

    // Handle unauthorized
    if (response.status === 401) {
      const body = await safeParseJson(response);
      if ((body?.data as Record<string, unknown>)?.redirectUrl) {
        window.location.replace((body?.data as Record<string, unknown>).redirectUrl as string);
      }
      return rejectWithValue?.(body, { status: response.status });
    }

    const parsedData = isArrayBuffer ? await response.arrayBuffer() : await safeParseJson(response);

    // Reject on 4xx/5xx to match axios behavior
    if (!response.ok && rejectWithValue) {
      return rejectWithValue(parsedData, { status: response.status });
    }

    return { data: parsedData, status: response.status };
  } catch (error: unknown) {
    // fetch only throws on network errors
    return rejectWithValue?.(error instanceof Error ? error.message : error);
  }
};

/**
 * Streaming counterpart of `reusableFetch`: same URL prefix, headers, credentials and single CSRF retry, but resolves
 * with the raw `Response` so the caller can read its body as a stream. It never redirects, parses or rejects on
 * status; the caller decides what each status means. Rejects only on network errors or when `signal` aborts.
 */
export const reusableStreamFetch = async (
  url: string,
  method: string,
  data: unknown,
  { signal, accept }: { signal: AbortSignal; accept: string },
): Promise<Response> => {
  await waitForCsrfToken();

  const fullUrl = `${import.meta.env.VITE_API_URL}/${url}`;
  const buildOptions = (): RequestInit => ({
    method: method.toUpperCase(),
    headers: { ...headers(), Accept: accept },
    credentials: "include",
    body: JSON.stringify(data ?? {}),
    // A redirect here means the session has gone; surface it to the caller rather than following it.
    redirect: "manual",
    signal,
  });

  const response = await fetch(fullUrl, buildOptions());
  if (response.status !== 403) return response;

  // Read a copy, so the original body is still available to the caller if this is not a CSRF failure.
  const body = await safeParseJson(response.clone());
  if (!isCsrfError(response.status, body)) return response;

  await response.body?.cancel().catch(() => undefined);
  await fetchCsrfToken();
  return fetch(fullUrl, buildOptions());
};

export const reusableWithoutTokenFetch = async (
  url: string,
  method: string,
  data: unknown,
  rejectWithValue: RejectWithValue,
): Promise<FetchResult | unknown> => {
  await waitForCsrfToken();

  const fullUrl = `${import.meta.env.VITE_API_URL}/${url}`;
  const upperMethod = method.toUpperCase();

  const options: RequestInit = {
    method: upperMethod,
    headers: headers({ includeStoredToken: false }),
    credentials: "include",
  };

  if (upperMethod !== "GET" && upperMethod !== "HEAD") {
    options.body = JSON.stringify(data || "");
  }

  try {
    const response = await fetch(fullUrl, options);

    // Handle CSRF token expiry
    if (response.status === 403) {
      const body = await safeParseJson(response);
      if (isCsrfError(response.status, body)) {
        await fetchCsrfToken();
        const retryOptions: RequestInit = { ...options, headers: headers({ includeStoredToken: false }) };
        const retryResponse = await fetch(fullUrl, retryOptions);
        const retryData = await safeParseJson(retryResponse);
        if (!retryResponse.ok) {
          return rejectWithValue(retryData);
        }
        return { data: retryData, status: retryResponse.status };
      }
      return rejectWithValue(body);
    }

    if (response.status === 401) {
      const body = await safeParseJson(response);
      return rejectWithValue(body);
    }

    const parsedData = await safeParseJson(response);

    // Reject on 4xx/5xx to match axios behavior
    if (!response.ok) {
      return rejectWithValue(parsedData);
    }

    const redirectUrl = getAuthRedirectUrl(parsedData);
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }

    return { data: parsedData, status: response.status };
  } catch (error: unknown) {
    return rejectWithValue(error instanceof Error ? error.message : error);
  }
};

export const reusableFormDataFetch = async (
  url: string,
  method: string,
  formData: FormData | null,
  rejectWithValue: RejectWithValue,
  isTokenRequired = true,
  tokenOverride?: string,
): Promise<FetchResult | unknown> => {
  await waitForCsrfToken();

  const token = tokenOverride ?? getStoredAuthToken();
  const csrfToken = sessionStorage.getItem("csrfToken");

  // Do NOT set Content-Type for FormData - the browser auto-sets it with the correct boundary
  const formDataHeaders: Record<string, string> = {
    ...(isTokenRequired && token ? { "x-access-token": token } : {}),
    ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
  };

  try {
    if (formData) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/${url}`, {
        method: method,
        headers: formDataHeaders,
        body: formData,
        credentials: "include",
      });

      // Handle CSRF token expiry
      if (response.status === 403) {
        const body = await safeParseJson(response);
        if (isCsrfError(response.status, body)) {
          await fetchCsrfToken();
          const updatedCsrfToken = sessionStorage.getItem("csrfToken");
          const retryHeaders: Record<string, string> = {
            ...formDataHeaders,
            ...(updatedCsrfToken ? { "x-csrf-token": updatedCsrfToken } : {}),
          };
          const retryResponse = await fetch(`${import.meta.env.VITE_API_URL}/${url}`, {
            method: method,
            headers: retryHeaders,
            body: formData,
            credentials: "include",
          });
          const retryData = await safeParseJson(retryResponse);
          if (!retryResponse.ok) {
            return rejectWithValue(retryData);
          }
          return { data: retryData, status: retryResponse.status };
        }
        return rejectWithValue(body);
      }

      if (response.status === 401) {
        return rejectWithValue(await safeParseJson(response));
      }

      const parsedData = await safeParseJson(response);

      // Reject on 4xx/5xx to match axios behavior
      if (!response.ok) {
        return rejectWithValue(parsedData);
      }

      return { data: parsedData, status: response.status };
    }
  } catch (error: unknown) {
    return rejectWithValue(error instanceof Error ? error.message : error);
  }
};
